import { useMemo } from 'react'

import { getDisplayStep, type DocumentStep, type UIDocumentStep } from '~/entities/document'
import { cn } from '~/shared/lib/cn'
import { LocalIcon, Text } from '~/shared/ui'
import './step-tracker.scss'

type StepStatusMapKey = UIDocumentStep | DocumentStep

function getStepStatusMap({
  isTaggingEnabled,
  isEob,
}: {
  isTaggingEnabled: boolean
  isEob: boolean
}): Record<StepStatusMapKey, Record<number, number>> {
  if (isEob) {
    return {
      clustering: { 0: 0, 1: 1 },
      classification: { 0: 2, 1: 3 },
      eob_payment_deposit: { 0: 4, 1: 5 },
      eob_duplication: { 0: 6, 1: 7 },
      eob_processing: { 0: 8, 1: 9 },
      finished: { 0: 10, 1: 11 },
      data: {},
      carc_rarc: {},
      patient: {},
      processing: {},
    }
  }
  if (isTaggingEnabled) {
    return {
      clustering: { 0: 0, 1: 1 },
      classification: { 0: 2, 1: 3 },
      data: { 0: 4, 1: 5 },
      carc_rarc: { 0: 6, 1: 7 }, // Same as tagging (API name)
      patient: { 0: 8, 1: 9 },
      finished: { 0: 10, 1: 11 },
      processing: {},
      eob_payment_deposit: {},
      eob_duplication: {},
      eob_processing: {},
    }
  }
  return {
    clustering: { 0: 0, 1: 1 },
    classification: { 0: 2, 1: 3 },
    data: { 0: 4, 1: 5 },
    patient: { 0: 6, 1: 7 },
    finished: { 0: 8, 1: 9 },
    carc_rarc: {},
    processing: {},
    eob_payment_deposit: {},
    eob_duplication: {},
    eob_processing: {},
  }
}

export function StepTracker({
  step,
  hasReviewer,
  maxStepsCount,
  isTaggingEnabled = false,
  isEob = false,
}: {
  step: UIDocumentStep | DocumentStep
  hasReviewer: boolean
  maxStepsCount?: number
  isTaggingEnabled?: boolean
  isEob?: boolean
}) {
  const animationDuration = useMemo(() => 1.2 + Math.random() * 1, [])

  if (step === 'processing') {
    const segmentCount =
      isTaggingEnabled || isEob
        ? maxStepsCount === undefined
          ? 5
          : Math.ceil(maxStepsCount / 2)
        : maxStepsCount === undefined
          ? 4
          : Math.ceil(maxStepsCount / 2)

    return (
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <Text
            className="text-text-secondary"
            appearance="OVERLINE"
          >
            {getDisplayStep(step)}
          </Text>
          <LocalIcon
            className="text-text-secondary"
            name="opti"
            size="18"
          />
        </div>
        <div className="flex w-fit gap-1 py-1">
          {Array.from({ length: segmentCount }).map((_, i) => (
            <div
              key={i}
              className="bg-content-low-emphasis relative h-1 w-6 overflow-hidden rounded-full"
            >
              <div
                className="bg-text-secondary absolute top-0 h-1 w-6 rounded-full"
                style={
                  {
                    '--track-width': `${segmentCount * 28 - 4}px`,
                    animation: `processing-slide ${animationDuration}s ease-in-out infinite`,
                    left: `${-(i * 28)}px`,
                  } as React.CSSProperties
                }
              />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const stepStatusMap = getStepStatusMap({ isTaggingEnabled, isEob })
  const fillLevel = stepStatusMap[step][Number(hasReviewer)]

  return (
    <div className="flex flex-col">
      <Text
        className={cn('text-text-secondary', {
          'text-content-medium-emphasis': !hasReviewer,
        })}
        appearance="OVERLINE"
      >
        {getDisplayStep(step)}
      </Text>
      <div className="flex gap-1 py-1">
        {(maxStepsCount === undefined || maxStepsCount >= 1) && (
          <div className="bg-content-low-emphasis h-1 w-6 rounded-full">
            <div
              className={cn('bg-primary-main hidden h-1 rounded-full', {
                block: fillLevel > 0,
                'w-3': fillLevel === 1,
              })}
            />
          </div>
        )}
        {(maxStepsCount === undefined || maxStepsCount >= 3) && (
          <div className="bg-content-low-emphasis h-1 w-6 rounded-full">
            <div
              className={cn('bg-primary-main hidden h-1 rounded-full', {
                block: fillLevel > 2,
                'w-3': fillLevel === 3,
              })}
            />
          </div>
        )}
        {(maxStepsCount === undefined || maxStepsCount >= 5) && (
          <div className="bg-content-low-emphasis h-1 w-6 rounded-full">
            <div
              className={cn('bg-primary-main hidden h-1 rounded-full', {
                block: fillLevel > 4,
                'w-3': fillLevel === 5,
              })}
            />
          </div>
        )}
        {(maxStepsCount === undefined || maxStepsCount >= 7) && (
          <div className="bg-content-low-emphasis h-1 w-6 rounded-full">
            <div
              className={cn('bg-primary-main hidden h-1 rounded-full', {
                block: fillLevel > 6,
                'w-3': fillLevel === 7,
              })}
            />
          </div>
        )}
        {(isTaggingEnabled || isEob) && (maxStepsCount === undefined || maxStepsCount >= 9) && (
          <div className="bg-content-low-emphasis h-1 w-6 rounded-full">
            <div
              className={cn('bg-primary-main hidden h-1 rounded-full', {
                block: fillLevel > 8,
                'w-3': fillLevel === 9,
              })}
            />
          </div>
        )}
      </div>
    </div>
  )
}
